import { useEffect, useState } from 'react';
import { api, apiDownload, ApiError } from '../lib/api';
import { useToast } from '../context/ToastContext';

interface Deadline {
  id: string; label: string; kind: 'once' | 'monthly' | 'yearly';
  due_date?: string | null; day_of_month?: number | null; month_of_year?: number | null;
  notify_days_before: number; active: boolean; next_due?: string; days_until?: number;
}

interface DeclarationTotals { count: number; totals: Record<string, number>; lots?: string[]; }
interface Declaration {
  id: string; period: string; signed_by?: string | null; signed_at?: string | null;
  content: { wash_batches: DeclarationTotals; receptions: DeclarationTotals; deliveries: DeclarationTotals };
}

function currentPeriod(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function Legal() {
  const toast = useToast();

  const [upcoming, setUpcoming] = useState<Deadline[]>([]);
  const [all, setAll] = useState<Deadline[]>([]);
  const [label, setLabel] = useState('');
  const [kind, setKind] = useState<'once' | 'monthly' | 'yearly'>('monthly');
  const [dueDate, setDueDate] = useState('');
  const [dayOfMonth, setDayOfMonth] = useState('5');
  const [monthOfYear, setMonthOfYear] = useState('1');
  const [notifyDays, setNotifyDays] = useState('7');
  const [dlMsg, setDlMsg] = useState<{ text: string; err: boolean } | null>(null);

  const [period, setPeriod] = useState(currentPeriod());
  const [decl, setDecl] = useState<Declaration | null>(null);
  const [declMsg, setDeclMsg] = useState<{ text: string; err: boolean } | null>(null);
  const [busy, setBusy] = useState(false);

  function loadDeadlines() {
    api<Deadline[]>('/v1/legal-deadlines').then(setUpcoming).catch(() => {});
    api<Deadline[]>('/v1/legal-deadlines/all').then(setAll).catch(() => {});
  }
  useEffect(() => { loadDeadlines(); }, []);

  async function addDeadline(e: React.FormEvent) {
    e.preventDefault();
    if (!label.trim()) { setDlMsg({ text: 'Indiquez un libellé.', err: true }); return; }
    const body: Record<string, unknown> = { label: label.trim(), kind, notify_days_before: parseInt(notifyDays, 10) || 7 };
    if (kind === 'once') {
      if (!dueDate) { setDlMsg({ text: 'Indiquez la date.', err: true }); return; }
      body.due_date = dueDate;
    } else {
      body.day_of_month = parseInt(dayOfMonth, 10);
      if (kind === 'yearly') body.month_of_year = parseInt(monthOfYear, 10);
    }
    try {
      await api('/v1/legal-deadlines', { method: 'POST', body });
      setDlMsg({ text: 'Échéance ajoutée.', err: false });
      toast('Échéance légale ajoutée');
      setLabel(''); setDueDate('');
      loadDeadlines();
    } catch (e) { setDlMsg({ text: e instanceof ApiError ? e.message : 'Erreur', err: true }); }
  }

  async function toggleDeadline(id: string, active: boolean) {
    try {
      await api(`/v1/legal-deadlines/${id}`, { method: 'PATCH', body: { active: !active } });
      loadDeadlines();
    } catch (e) { setDlMsg({ text: e instanceof ApiError ? e.message : 'Erreur', err: true }); }
  }

  async function loadDeclaration() {
    setBusy(true); setDeclMsg(null);
    try { setDecl(await api<Declaration>(`/v1/declarations/${period}`)); }
    catch (e) { setDeclMsg({ text: e instanceof ApiError ? e.message : 'Erreur de chargement.', err: true }); }
    finally { setBusy(false); }
  }
  useEffect(() => { loadDeclaration(); }, [period]);

  async function sign() {
    if (!confirm('Confirmer la signature ? Cette déclaration sera figée et ne pourra plus être régénérée.')) return;
    setBusy(true);
    try {
      const r = await api<Declaration>(`/v1/declarations/${period}/sign`, { method: 'POST' });
      setDecl(r);
      setDeclMsg({ text: 'Déclaration signée.', err: false });
      toast('Déclaration mensuelle signée');
    } catch (e) { setDeclMsg({ text: e instanceof ApiError ? e.message : 'Erreur', err: true }); }
    finally { setBusy(false); }
  }

  async function downloadPdf() {
    try { await apiDownload(`/v1/declarations/${period}/pdf`, `declaration-${period}.pdf`); }
    catch (e) { setDeclMsg({ text: e instanceof ApiError ? e.message : 'Téléchargement impossible', err: true }); }
  }

  function fmtTotals(t: Record<string, number>) {
    const entries = Object.entries(t);
    return entries.length ? entries.map(([k, v]) => `${k} : ${v}`).join(' — ') : '—';
  }

  return (
    <>
      <div className="pagehead"><h1>Conformité</h1></div>

      <div className="card">
        <h2>Échéances légales à venir</h2>
        {!upcoming.length && <p className="muted">Aucune échéance dans les 60 prochains jours.</p>}
        {upcoming.length > 0 && (
          <table>
            <thead><tr><th>Échéance</th><th>Date</th><th>Dans</th></tr></thead>
            <tbody>
              {upcoming.map((d) => (
                <tr key={d.id}>
                  <td>{d.label}</td>
                  <td>{d.next_due}</td>
                  <td>{d.days_until} jour(s)</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card">
        <h2>Gérer les échéances</h2>
        <form onSubmit={addDeadline}>
          <label>Libellé</label>
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="ex. Déclaration ONSSA mensuelle" />
          <div className="row2">
            <div>
              <label>Type</label>
              <select value={kind} onChange={(e) => setKind(e.target.value as 'once' | 'monthly' | 'yearly')}>
                <option value="once">Ponctuelle</option>
                <option value="monthly">Mensuelle</option>
                <option value="yearly">Annuelle</option>
              </select>
            </div>
            <div><label>Rappel X jours avant</label><input type="number" min={0} value={notifyDays} onChange={(e) => setNotifyDays(e.target.value)} /></div>
          </div>
          {kind === 'once' && (<><label>Date</label><input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} /></>)}
          {(kind === 'monthly' || kind === 'yearly') && (
            <div className="row2">
              <div><label>Jour du mois</label><input type="number" min={1} max={28} value={dayOfMonth} onChange={(e) => setDayOfMonth(e.target.value)} /></div>
              {kind === 'yearly' && (<div><label>Mois</label><input type="number" min={1} max={12} value={monthOfYear} onChange={(e) => setMonthOfYear(e.target.value)} /></div>)}
            </div>
          )}
          <div className="row" style={{ marginTop: '0.6rem' }}><button type="submit" className="primary">Ajouter l'échéance</button></div>
          {dlMsg && <div className={dlMsg.err ? 'err' : 'ok'}>{dlMsg.text}</div>}
        </form>
        {all.length > 0 && (
          <div className="tblwrap" style={{ marginTop: '0.8rem' }}>
            <table>
              <thead><tr><th>Libellé</th><th>Type</th><th>Rappel</th><th>Statut</th><th></th></tr></thead>
              <tbody>
                {all.map((d) => (
                  <tr key={d.id}>
                    <td>{d.label}</td>
                    <td>{d.kind}</td>
                    <td>{d.notify_days_before} j avant</td>
                    <td>{d.active ? 'Active' : 'Désactivée'}</td>
                    <td><button type="button" className="small" onClick={() => toggleDeadline(d.id, d.active)}>{d.active ? 'Désactiver' : 'Activer'}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <h2>Déclaration mensuelle signée</h2>
        <label>Mois</label>
        <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} />
        {busy && <p className="muted">Chargement...</p>}
        {decl && !busy && (
          <>
            <p style={{ marginTop: '0.6rem' }}>
              <strong>Lots de lavage ONSSA</strong> : {decl.content.wash_batches.count} lot(s) — {fmtTotals(decl.content.wash_batches.totals)}
            </p>
            <p><strong>Réceptions</strong> : {decl.content.receptions.count} — {fmtTotals(decl.content.receptions.totals)}</p>
            <p><strong>Remises clients</strong> : {decl.content.deliveries.count} — {fmtTotals(decl.content.deliveries.totals)}</p>
            {decl.signed_at ? (
              <p className="ok">Certifié par {decl.signed_by} le {decl.signed_at}</p>
            ) : (
              <p className="muted">Brouillon — non signé.</p>
            )}
            <div className="row" style={{ marginTop: '0.6rem', gap: 8 }}>
              {!decl.signed_at && <button type="button" className="primary" onClick={sign} disabled={busy}>Je certifie l'exactitude de cette déclaration</button>}
              <button type="button" className="small" onClick={downloadPdf}>Télécharger le PDF</button>
            </div>
          </>
        )}
        {declMsg && <div className={declMsg.err ? 'err' : 'ok'}>{declMsg.text}</div>}
      </div>
    </>
  );
}
